import { CheckCircle, XCircle } from 'lucide-react'
import { AccessTierBadge, CriteriaStatusBadge } from './Badges'
import { formatDate } from './helpers'
import type { LicenseCriteria } from '../../types'

const HEADERS = ['Organisation', 'Reference', 'Tier', 'Seats', 'Valid From', 'Status'] as const

const TABLE_COL_SPAN = 7

interface CriteriaRowProps {
  entry: LicenseCriteria
  getOrgName: (orgId: string) => string
  verifyingId: string | null
  invalidatingId: string | null
  onVerify: (id: string) => void
  onInvalidate: (id: string) => void
}

function PendingActions({
  entry,
  verifyingId,
  invalidatingId,
  onVerify,
  onInvalidate,
}: CriteriaRowProps): React.ReactElement {
  return (
    <>
      <button
        onClick={() => {
          onVerify(entry.id)
        }}
        disabled={verifyingId === entry.id}
        className="rounded p-1.5 text-green-500 hover:bg-green-50 hover:text-green-700 disabled:opacity-50 dark:text-green-400 dark:hover:bg-green-900/20 dark:hover:text-green-300"
        title="Verify"
      >
        <CheckCircle className="h-4 w-4" />
      </button>
      <button
        onClick={() => {
          onInvalidate(entry.id)
        }}
        disabled={invalidatingId === entry.id}
        className="rounded p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
        title="Invalidate"
      >
        <XCircle className="h-4 w-4" />
      </button>
    </>
  )
}

function CriteriaRow(props: CriteriaRowProps): React.ReactElement {
  const { entry, getOrgName } = props
  return (
    <tr className="hover:bg-stone-50 dark:hover:bg-stone-700/50">
      <td className="px-4 py-3 text-sm text-stone-700 dark:text-stone-300">
        {getOrgName(entry.organisationId)}
      </td>
      <td className="px-4 py-3 text-sm text-stone-600 dark:text-stone-400">
        {entry.externalReference !== '' ? entry.externalReference : '-'}
      </td>
      <td className="px-4 py-3">
        <AccessTierBadge tier={entry.accessTier} />
      </td>
      <td className="px-4 py-3 text-sm text-stone-700 dark:text-stone-300">{entry.seatsGranted}</td>
      <td className="px-4 py-3 text-sm text-stone-600 dark:text-stone-400">
        {formatDate(entry.validFrom)}
      </td>
      <td className="px-4 py-3">
        <CriteriaStatusBadge status={entry.status} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          {entry.status === 'pending' && <PendingActions {...props} />}
        </div>
      </td>
    </tr>
  )
}

interface CriteriaTableProps {
  criteria: LicenseCriteria[]
  getOrgName: (orgId: string) => string
  verifyingId: string | null
  invalidatingId: string | null
  onVerify: (id: string) => void
  onInvalidate: (id: string) => void
}

function TableHead(): React.ReactElement {
  return (
    <thead className="border-b border-stone-200 bg-stone-50 dark:border-stone-700 dark:bg-stone-800/50">
      <tr>
        {HEADERS.map((header) => (
          <th
            key={header}
            className="px-4 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400"
          >
            {header}
          </th>
        ))}
        <th className="px-4 py-3 text-right text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          Actions
        </th>
      </tr>
    </thead>
  )
}

export function CriteriaTable(props: CriteriaTableProps): React.ReactElement {
  const { criteria, getOrgName, verifyingId, invalidatingId, onVerify, onInvalidate } = props
  return (
    <div className="overflow-hidden rounded-lg border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800">
      <div className="overflow-x-auto">
        <table className="w-full">
          <TableHead />
          <tbody className="divide-y divide-stone-200 dark:divide-stone-700">
            {criteria.length === 0 ? (
              <tr>
                <td
                  colSpan={TABLE_COL_SPAN}
                  className="px-4 py-8 text-center text-stone-500 dark:text-stone-400"
                >
                  No license criteria found
                </td>
              </tr>
            ) : (
              criteria.map((entry) => (
                <CriteriaRow
                  key={entry.id}
                  entry={entry}
                  getOrgName={getOrgName}
                  verifyingId={verifyingId}
                  invalidatingId={invalidatingId}
                  onVerify={onVerify}
                  onInvalidate={onInvalidate}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
